import { t } from "./catalogue.js";

/**
 * A refusal, as a sentence the reader can act on.
 *
 * ==========================================================================================
 * IT READS THE CODE, NEVER THE MESSAGE
 * ==========================================================================================
 *
 * This is the whole of feature 021's answer to "domain error sentences are built inside the domain
 * layer". They stay there. A bounded context cannot reach a catalogue and must not try — it would be
 * reaching for a React package's subpath from code that must run without React at all — and it does
 * not have to, because every refusal already carries a stable `ErrorCode`.
 *
 * `packages/api-client/src/api-error.ts` has said so since it was written: "Components branch on
 * `code`, never on `message`. A message is copy — it gets reworded, localised, or softened — and a
 * UI that switches on one breaks silently when it does." This function is that instruction, applied
 * once instead of at thirty-three call sites.
 *
 * ==========================================================================================
 * IT DUCK-TYPES `ApiError` RATHER THAN IMPORTING IT
 * ==========================================================================================
 *
 * `@gladis/ui` depends on `@gladis/shared` and nothing else. Importing `@gladis/api-client` for one
 * `instanceof` would add a dependency to the design system so that it could read a string property —
 * and `instanceof` across package boundaries is fragile in ways a shape check is not.
 *
 * The shape it looks for is `{ code: string }`, which is exactly what it needs and no more.
 */

interface Coded {
  readonly code: string;
  readonly details?: Record<string, unknown>;
}

function codeOf(error: unknown): string | null {
  if (typeof error !== "object" || error === null) return null;
  const code = (error as Partial<Coded>).code;
  return typeof code === "string" && code.length > 0 ? code : null;
}

/**
 * The sentence for a refusal, in the language in force.
 *
 * FALLS BACK TO `error.unexpected`, NOT TO `error.message`. Rendering the server's own sentence when
 * the catalogue has no entry would look like a working fallback and would be a language leak: an
 * English sentence appearing, unpredictably, inside a Dutch page. A generic sentence in the right
 * language is the honest failure, and `error-catalogue.test.ts` makes it unreachable for any code
 * the enum declares.
 *
 * SYS_INTERNAL, SYS_DATABASE and SYS_UNAVAILABLE reach it BY DESIGN rather than by omission — see
 * the note in `en.ts`. A broken invariant has no next step to offer, and pretending otherwise is
 * worse than saying so.
 */
export function errorSentence(error: unknown): string {
  const code = codeOf(error);
  if (code === null) return t("error.unexpected");

  /*
   * A REASON BEATS A CODE, when the refusal carries one.
   *
   * Eight date codes across five contexts share four distinct problems — malformed, not a date, too
   * early, too far — and the code alone cannot tell them apart. `validateIsoDay` puts its `reason`
   * into `details` for exactly this, and the values it needs travel with it: `{maxYearsAhead}` is
   * what keeps "more than 40 years away" as precise in Dutch as it is in English.
   */
  const details = (error as Partial<Coded>).details;
  const reason = typeof details?.["reason"] === "string" ? details["reason"] : null;
  if (reason !== null) {
    const byReason = t(`error.date.${reason}`, details as Record<string, string | number>);
    if (byReason !== `error.date.${reason}`) return byReason;
  }

  const sentence = t(`error.${code}`);

  // `t` returns the identifier when a key is absent. That is right for a developer reading a screen
  // and wrong for a member reading a refusal, so it is caught here rather than rendered.
  return sentence === `error.${code}` ? t("error.unexpected") : sentence;
}

/**
 * A category's name, in the language in force.
 *
 * ==========================================================================================
 * WHY THESE MOVED OUT OF `@gladis/shared`
 * ==========================================================================================
 *
 * The catalogue in `packages/shared/src/catalog/` held `{ value, label }` pairs. `value` is the
 * identifier the database stores and the Zod enum derives from; `label` was an English sentence
 * sitting beside it in the shared kernel — which `apps/api` imports, and which has no reader to show
 * a label to. One of the two belonged at the edge, and this feature's whole rule says which.
 *
 * `value` did not move, so `expenses.ts`'s Zod enum, the `expense_category` database enum and every
 * stored row are untouched. 009's rule that an eighteenth category costs two changes still holds —
 * a line in the catalogue and a line in a migration — and the catalogue is now this file's twin.
 */
export function expenseCategoryLabel(value: string): string {
  return t(`category.expense.${value}`);
}

export function incomeCategoryLabel(value: string): string {
  return t(`category.income.${value}`);
}
